<template>
  <div v-if="isOpen" class="modal-overlay" @click.self="close">
    <div class="modal-content">
      <button class="close-btn" @click="close">&times;</button>
      
      <div class="auth-header">
        <div class="auth-logo">GELT</div>
        <h2>Welcome Back</h2>
        <p>Sign in to access your account</p>
      </div>

      <form @submit.prevent="submitForm" class="auth-form">
        <BaseInput
          v-model="form.account"
          type="text"
          label="Username"
          :required="true"
          :error="errors.account"
          placeholder="Enter username or email"
        />

        <BaseInput
          v-model="form.password"
          type="password"
          label="Password"
          :required="true"
          :error="errors.password"
          placeholder="Enter your password"
        />

        <div class="form-options">
          <label class="remember-me">
            <input type="checkbox" v-model="form.rememberMe" />
            <span>Remember me</span>
          </label>
          <a href="#" class="forgot-link">Forgot password?</a>
        </div>

        <button type="submit" class="btn btn-primary btn-lg" :disabled="isSubmitting" style="width: 100%">
          <span v-if="isSubmitting">Signing in...</span>
          <span v-else>Sign In</span>
        </button>

        <div v-if="submitError" class="alert alert-danger mt-3">
          {{ submitError }}
        </div>
      </form>

      <div class="auth-footer">
        <!-- <p>Don't have an account? <router-link to="/register" @click="close">Create Account</router-link></p> -->
        <p class="lawyer-link">Are you a legal professional? <router-link to="/join-law" @click="close">Join GELT</router-link></p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import BaseInput from '@/components/forms/BaseInput.vue'

export default {
  name: 'LoginModal',
  components: { BaseInput },
  setup() {
    const store = useStore()
    const router = useRouter()
    const route = useRoute()
    
    const isOpen = computed(() => store.state.showLoginModal)
    const isSubmitting = ref(false)
    const submitError = ref('')
    
    const form = reactive({
      account: '',
      password: '',
      rememberMe: false
    })
    
    const errors = reactive({})
    
    const close = () => {
      store.commit('TOGGLE_LOGIN_MODAL', false)
      // Reset form on close
      form.account = ''
      form.password = ''
      submitError.value = ''
    }
    
    const validateForm = () => {
      let isValid = true
      Object.keys(errors).forEach(k => delete errors[k])
      
      if (!form.account.trim()) { errors.account = 'Username or email is required'; isValid = false }
      if (!form.password) { errors.password = 'Password is required'; isValid = false }
      
      return isValid
    }
    
    const submitForm = async () => {
      if (!validateForm()) return
      
      isSubmitting.value = true
      submitError.value = ''
      
      try {
        await store.dispatch('login', {
          email: form.account,
          password: form.password,
          rememberMe: form.rememberMe
        })
        
        close()
        
        // Redirect if needed - Default to Home page '/' as per your request
        const redirectPath = route.query.redirect || '/'
        if (route.path !== redirectPath) {
          await router.push(redirectPath)
        }
        
        // Force refresh to clear UI state and ensure auth tokens are picked up
        window.location.reload()
      } catch (error) {
        submitError.value = error.response?.data?.message || error.response?.data?.error || 'Invalid account or password'
      } finally {
        isSubmitting.value = false
      }
    }
    
    return { isOpen, close, form, errors, isSubmitting, submitError, submitForm }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: var(--white);
  padding: 2.5rem;
  border-radius: var(--radius-lg);
  width: 100%;
  max-width: 420px;
  position: relative;
  box-shadow: var(--shadow-xl);
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--gray-400);
  cursor: pointer;
  padding: 0.5rem;
  line-height: 1;
}

.close-btn:hover { color: var(--gray-600); }

.auth-header { text-align: center; margin-bottom: 2rem; }
.auth-logo { font-size: 1.5rem; font-weight: 600; color: var(--primary); margin-bottom: 1rem; }
.auth-header h2 { font-size: 1.75rem; margin-bottom: 0.5rem; }
.auth-header p { color: var(--gray-500); margin-bottom: 0; }

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.remember-me {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: var(--font-size-sm);
  color: var(--gray-600);
}

.forgot-link {
  font-size: var(--font-size-sm);
  color: var(--primary);
}

.auth-footer {
  text-align: center;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--gray-100);
}

.auth-footer p { margin-bottom: 0.5rem; color: var(--gray-500); }
.auth-footer a { color: var(--primary); font-weight: 500; }

.lawyer-link {
  font-size: 0.9rem;
}
</style>
